import { useEffect, useRef, useState, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Scale } from 'lucide-react';
import { Footer } from '@/sections/Footer';
import { SEO } from '@/components/SEO';
import { LEGAL_DOCS, type LegalBlock, type LegalDoc } from '@/legal/types';

// Renders **bold** spans inside legal text.
function Inline({ text }: { text: string }) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return (
    <>
      {parts.map((part, i) =>
        i % 2 === 1 ? (
          <strong key={i} className="font-semibold text-text-primary">
            {part}
          </strong>
        ) : (
          <Fragment key={i}>{part}</Fragment>
        )
      )}
    </>
  );
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.t) {
    case 'p':
      return (
        <p className="text-sm text-text-secondary leading-relaxed mb-4">
          <Inline text={block.text} />
        </p>
      );
    case 'h3':
      return (
        <h3 className="font-heading text-base font-semibold text-text-primary mt-6 mb-3">
          <Inline text={block.text} />
        </h3>
      );
    case 'ul':
      return (
        <ul className="space-y-2 mb-4 pl-1">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-2 text-sm text-text-secondary leading-relaxed">
              <span className="w-1.5 h-1.5 rounded-full bg-brand flex-shrink-0 mt-[7px]" />
              <span>
                <Inline text={item} />
              </span>
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className="space-y-2 mb-4 pl-1 list-none">
          {block.items.map((item, i) => (
            <li key={i} className="flex gap-3 text-sm text-text-secondary leading-relaxed">
              <span className="font-heading font-semibold text-brand flex-shrink-0">
                {String.fromCharCode(97 + i)})
              </span>
              <span>
                <Inline text={item} />
              </span>
            </li>
          ))}
        </ol>
      );
    case 'note':
      return (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 mb-4">
          <p className="text-sm text-amber-900 leading-relaxed">
            <Inline text={block.text} />
          </p>
        </div>
      );
    case 'table':
      return (
        <div className="overflow-x-auto mb-4 rounded-2xl border border-gray-100">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-bg-light">
                {block.head.map((h, i) => (
                  <th
                    key={i}
                    className="text-left font-heading font-semibold text-text-primary px-4 py-3"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i} className="border-t border-gray-100">
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-3 text-text-secondary align-top">
                      <Inline text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

export function LegalPage({ doc }: { doc: LegalDoc }) {
  const [activeId, setActiveId] = useState<string>('');
  const contentRef = useRef<HTMLDivElement>(null);

  // Scroll spy: highlight the TOC entry of the section currently in view.
  useEffect(() => {
    const sections = contentRef.current?.querySelectorAll('section[id]');
    if (!sections || sections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -60% 0px', threshold: 0 }
    );

    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, [doc.slug]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toc = doc.sections.map((s) => ({ id: s.id, label: `${s.number}. ${s.title}` }));

  return (
    <main className="bg-bg-light min-h-screen">
      <SEO
        title={`${doc.title} | Mi Envío Cuba`}
        description={doc.seoDescription}
        path={doc.slug}
      />

      {/* Header */}
      <div className="pt-32 pb-10 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="w-4 h-4 text-brand" />
            <span className="text-xs font-semibold text-brand uppercase tracking-widest">
              Legal
            </span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary mb-4">
            {doc.title}
          </h1>
          <p className="text-sm text-text-muted">
            Versión {doc.version} · Última actualización: {doc.updated} · {doc.entity}
          </p>
        </div>
      </div>

      <div className="pb-24 px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-5xl mx-auto lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-10 lg:items-start">
          {/* Mobile TOC */}
          <details className="lg:hidden bg-white rounded-[20px] shadow-card mb-6 overflow-hidden">
            <summary className="px-5 py-4 font-heading font-semibold text-text-primary cursor-pointer select-none">
              Contenido
            </summary>
            <nav className="px-3 pb-4">
              {toc.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollTo(item.id)}
                  className="block w-full text-left px-2 py-1.5 text-sm text-text-secondary hover:text-brand"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </details>

          {/* Desktop sidebar TOC */}
          <aside className="hidden lg:block sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto">
            <div className="bg-white rounded-[24px] shadow-card p-4">
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider px-2 mb-3">
                Contenido
              </p>
              <nav className="space-y-0.5">
                {toc.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    className={`block w-full text-left px-3 py-2 rounded-xl text-sm transition-colors ${
                      activeId === item.id
                        ? 'bg-brand/10 text-brand font-medium'
                        : 'text-text-secondary hover:text-text-primary hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Document body */}
          <div ref={contentRef} className="bg-white rounded-[28px] shadow-card p-6 sm:p-8 lg:p-10">
            {doc.sections.map((section) => (
              <section key={section.id} id={section.id} className="scroll-mt-28 mb-10 last:mb-0">
                <h2 className="font-heading text-xl font-semibold text-text-primary mb-4 pb-3 border-b border-gray-100">
                  {section.number}. {section.title}
                </h2>
                {section.blocks.map((block, i) => (
                  <Block key={i} block={block} />
                ))}
              </section>
            ))}

            {doc.closing && (
              <p className="text-xs text-text-muted mt-8 pt-6 border-t border-gray-100">
                {doc.closing}
              </p>
            )}
          </div>
        </div>

        {/* Cross-links to the rest of the legal set */}
        <div className="max-w-5xl mx-auto mt-10">
          <div className="bg-white rounded-[24px] shadow-card p-6">
            <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider mb-4">
              Otras políticas
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {LEGAL_DOCS.filter((d) => d.slug !== doc.slug).map((d) => (
                <Link
                  key={d.slug}
                  to={d.slug}
                  className="text-sm text-brand hover:underline"
                >
                  {d.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
